import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Profile } from 'types/profile';
import { Collaborator } from 'types/collaborator.types';
import { ContactCardLoader } from './ContactCardLoader';
import { getProfileData, getRepresentativesDate } from './utils';
import { modalBodySx } from './styles';
import { BackgroundImage } from '../ProfileBar/BackgroundImage';

export interface ContactCardDialogComponentProps {
  onClose: () => void;
  isOpen: boolean;
  isLoading: boolean;
  profile?: Profile;
  collaborator: Pick<Collaborator, 'profile' | 'profileCard'>;
}

export const ContactCardDialogComponent: React.FC<ContactCardDialogComponentProps> = ({
  isOpen,
  onClose,
  collaborator,
  profile,
  isLoading,
}) => {
  const representativesData = getRepresentativesDate(profile?.representatives);
  const profileData = getProfileData(isLoading, profile);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        variant="fitContent"
        data-testid="collaborator-contact-card-dialog"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxHeight="full" maxW={354} w="full">
          <ModalHeader>
            Profile Information
            <Text fontSize="sm" color="textSubdued">
              {collaborator.profile?.displayName}
            </Text>
          </ModalHeader>
          <ModalBody
            data-testid="dialog-content"
            maxHeight={isLoading ? 150 : 500}
            sx={modalBodySx}
          >
            <BackgroundImage collaborator={collaborator} />
            <Box mt={6}>
              {isLoading ? (
                <ContactCardLoader />
              ) : (
                <Collapse in>
                  {profileData.length === 0 &&
                  representativesData.length === 0 ? (
                    <VStack textAlign="center" pt={6}>
                      <Heading color="M.70" as="h6" size="sm">
                        No Collaborator information
                      </Heading>
                      <Text>
                        Collaborator has not added additional
                        <br />
                        profile information
                      </Text>
                    </VStack>
                  ) : (
                    <>
                      {profileData.map(({ key, label, value }) => (
                        <Flex key={key} minH={12}>
                          <Text w={100} color="textSubdued">
                            {label}
                          </Text>
                          <Box>
                            <Text w="fit-content">{value.primary}</Text>
                            {value.secondary && (
                              <Text
                                fontSize="xs"
                                color="textBase"
                                w="fit-content"
                              >
                                {value.secondary}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ))}
                      {representativesData.map(
                        ({ name, email, role, company, publisherIpi }) => (
                          <Flex key={name} my={1}>
                            <Text w={100} color="textSubdued">
                              {role}
                            </Text>
                            <Box w="fit-content">
                              <Text>{name}</Text>
                              <Text fontSize="xs" color="textSubdued">
                                {company}
                              </Text>
                              {publisherIpi && (
                                <Text fontSize="xs" color="textSubdued">
                                  IPI: {publisherIpi}
                                </Text>
                              )}
                              <Text fontSize="xs" color="textSubdued">
                                {email}
                              </Text>
                            </Box>
                          </Flex>
                        )
                      )}
                    </>
                  )}
                </Collapse>
              )}
            </Box>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button onClick={onClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
